import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Building, Article, ShieldCheck } from "@phosphor-icons/react";
import { MaskedLine, Reveal, Overline } from "../components/Kinetic";
import { TWO_A_URL } from "../components/EstimateCTA";
import { Seo, ORG_SCHEMA } from "../components/Seo";

const STANDARDS = [
  "Every guide answers a real question Chicago sellers actually ask.",
  "Original content only — nothing copied or spun from other websites.",
  "No invented statistics, fake reviews, or fake customer stories.",
  "Honest dates. If a guide was not updated, we do not claim it was.",
  "Working devices only. We never promote selling broken electronics.",
  "If a guide would not deserve to exist without a business mention, we do not publish it.",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About"
        description="Who operates Sell My Phone Chicago, our relationship with 2A Electronics, and the editorial standards behind every Buyback Guide."
        path="/about"
        jsonLd={[ORG_SCHEMA]}
      />

      <section className="pt-32 md:pt-44">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Overline>About This Site</Overline>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.0] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>A Chicago buyback resource</MaskedLine>
            <MaskedLine delay={0.3}>built on <span className="text-brand">usefulness.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#4A4A4A]">
              Sell My Phone Chicago exists to answer one set of questions well: how to sell your phone, laptop,
              tablet, watch, or console in Chicago — safely, privately, and for a fair price. Everything we
              publish is written to be useful even if you never sell a thing through us.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 md:px-12 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-black/10 bg-white p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-11" data-testid="about-who-card">
              <Building size={32} weight="duotone" className="text-brand" />
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">Who operates this site</h2>
              <p className="mt-4 text-base leading-[1.85] text-[#2a2a2a]">
                Sell My Phone Chicago is an electronics buyback information resource operated in association
                with{" "}
                <a href="https://2aelectronics.com/" target="_blank" rel="noopener" className="font-semibold text-brand underline underline-offset-2 hover:text-navy">
                  2A Electronics
                </a>
                , a Chicago electronics business. We disclose that relationship openly: when we feature
                2A Electronics in our guides — roughly once a week, when it is genuinely relevant — the
                feature is clearly labelled. This site is not an independent review publication, and we
                will never present it as one.
              </p>
              <p className="mt-4 text-base leading-[1.85] text-[#2a2a2a]">
                What that means in practice: the educational content stands on its own, business mentions
                are transparent, and links to 2A Electronics appear only where a real local option is useful
                to the reader.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl border border-black/10 bg-white p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-11" data-testid="about-standards-card">
              <Article size={32} weight="duotone" className="text-brand" />
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">Our editorial standards</h2>
              <ul className="mt-5 flex flex-col gap-3.5">
                {STANDARDS.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[15px] leading-relaxed text-black/85">
                    <CheckCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-cash" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F5F7] py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <ShieldCheck size={40} weight="duotone" className="mx-auto text-navy" />
          <Reveal>
            <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tighter md:text-4xl">
              One rule, everywhere on this site
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#4A4A4A]">
              We cover the sale of working electronics in good condition — and sealed, new-in-box devices.
              We do not buy, promote, or advise on selling broken devices. If a device has reached the end
              of its working life, the right destination is certified e-waste recycling, not a buyback counter.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid="about-estimate-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy">
                Get a Buyback Estimate
                <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link to="/guides" data-testid="about-guides-btn"
                className="rounded-full border border-black/15 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-white">
                Read the Guides
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
